import {
  ActionMenu,
  DotMenuHorizontalIcon,
  ActionMenuItem,
  BodyMedium,
  ChatIcon,
  ConditionalWrapper,
  LanguageSelector,
  Modal,
  theme,
  Tooltip,
  usePath,
  useToast,
  DeprecatedCloseIcon,
  DeprecatedShareIcon,
  DeprecatedSaveIcon,
  DeprecatedEditIcon,
} from '@arnold/common';
import styled from '@emotion/styled';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { useCloneTopicWithTopicGroupMutation, useCreateTestSurveyMutation } from '../../generated/hooks';
import ee from '../../lib/eventEmitter';
import { ButtonLink } from '../StyledComponents';
import { EditTopicGroupInfo } from './components/EditTopicGroupInfo';
import { DataNode } from './dataNode';
import { TranslationStatus, getNodeTranslationStatus, getTGAccessHeaderContext } from './utils';
import { ActionButton } from '../Common';

const StyledControlPanel = styled.div`
  background-color: ${theme.background.primary};
  padding: ${theme.spacing.f};
  display: flex;
  justify-content: space-between;
  z-index: 50;
`;

const StyledLinkButton = styled(ButtonLink)`
  display: flex;
  align-items: center;
  -webkit-logical-height: auto;
  padding: 0 ${theme.spacing.d};
  margin-left: ${theme.spacing.f};
`;

const ActionMenuWrap = styled.div`
  margin-left: ${theme.spacing.f};
`;

const TranslateAllButton = styled.button`
  background: none;
  border: 1px solid ${theme.colors.borderMain.default};
  border-radius: 6px;
  color: ${theme.colors.text.primary};
  font-size: 11px;
  font-weight: 600;
  padding: 2px ${theme.spacing.d};
  margin-left: ${theme.spacing.d};
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s;
  &:hover:not(:disabled) {
    background-color: ${theme.colors.backgroundBasic.hover};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const translationStatusBadgeColors: Record<Exclude<TranslationStatus, 'ok'>, string> = {
  missing: theme.colors.emotionDanger.default,
  same_as_source: theme.colors.emotionWarning.default,
  source_changed: theme.colors.emotionWarning.default,
};

const TranslationBadge = styled.div<{ worstStatus: Exclude<TranslationStatus, 'ok'> }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => translationStatusBadgeColors[props.worstStatus]};
  color: white;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  padding: 1px ${theme.spacing.d};
  margin-left: ${theme.spacing.d};
  white-space: nowrap;
`;

type ControlPanelProps = {
  languages: string[];
  selectedLanguage?: string;
  defaultTopicLanguageCode: string;
  topicGroupTranslation: string;
  handleLanguageChange: (language: string) => void;
  handleLanguageDelete: (language: string) => void;
  onClose: () => void;
  onSave: (isTesting: boolean) => Promise<string | undefined>;
  onClear: () => void;
  onRearrange: () => void;
  onDownloadTranslations: () => void;
  onUploadTranslations: () => void;
  onRegenerateReportOrder: () => void;
  shareLinks: {
    url: string;
    level: string;
  }[];
  topicId: string;
  topicGroupId: string;
  preview: boolean;
  setTopicGroupName: (name: string) => void;
  setTopicGroupDescription: (description: string) => void;
  topicGroupDescription?: string;
  isValid: boolean;
  isEditable: boolean;
  isCustom: boolean;
  freeOrgWithCustomTopic: boolean;
  limitedMode?: 'READ' | 'WRITE';
  nodes?: DataNode[];
  nodeStatusOverrides?: Map<string, string>;
  onTranslateAll?: () => Promise<void>;
  hasAiTranslation: boolean;
};

export const ControlPanel: FC<ControlPanelProps> = ({
  languages,
  selectedLanguage,
  defaultTopicLanguageCode,
  topicGroupTranslation,
  shareLinks,
  handleLanguageChange,
  handleLanguageDelete,
  topicId,
  topicGroupId,
  isValid,
  isEditable,
  isCustom,
  freeOrgWithCustomTopic,
  onClose,
  onSave,
  onClear,
  onRearrange,
  onRegenerateReportOrder,
  onDownloadTranslations,
  onUploadTranslations,
  preview,
  setTopicGroupName,
  setTopicGroupDescription,
  topicGroupDescription,
  limitedMode,
  nodes,
  nodeStatusOverrides,
  onTranslateAll,
  hasAiTranslation,
}) => {
  const [isTranslatingAll, setIsTranslatingAll] = useState(false);
  const { t } = useTranslation(['topicOverview', 'topicEditor', 'surveysOverview']);
  const [clearModalShow, setClearModalShow] = useState(false);

  const translationIssues = (nodes ?? []).reduce<{ missing: number; sameAsSource: number; sourceChanged: number }>(
    (acc, node) => {
      const override = nodeStatusOverrides?.get(node.id);
      const status =
        override != null
          ? (override as TranslationStatus)
          : getNodeTranslationStatus(node.data.originalQuestionDefinition, selectedLanguage ?? '');
      if (status === 'missing') acc.missing += 1;
      else if (status === 'same_as_source') acc.sameAsSource += 1;
      else if (status === 'source_changed') acc.sourceChanged += 1;
      return acc;
    },
    { missing: 0, sameAsSource: 0, sourceChanged: 0 },
  );
  const totalIssues = hasAiTranslation
    ? 0
    : translationIssues.missing + translationIssues.sameAsSource + translationIssues.sourceChanged;
  const worstStatus: Exclude<TranslationStatus, 'ok'> | null =
    translationIssues.missing > 0
      ? 'missing'
      : translationIssues.sourceChanged > 0 || translationIssues.sameAsSource > 0
        ? 'source_changed'
        : null;
  const { search } = useLocation();
  const history = useHistory();
  const { addToast } = useToast();
  const { topicEditor } = usePath();
  const [cloneTopicWithTopicGroup] = useCloneTopicWithTopicGroupMutation();
  const [createTestSurvey, { loading: loadingTestSurvey }] = useCreateTestSurveyMutation();
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const visibleShareLinks = preview ? shareLinks.filter((link) => link.level !== 'WRITE') : shareLinks;

  const createSurvey = async () => {
    const testTopicId = await onSave(true);
    if (testTopicId == null) return;
    try {
      const { data } = await createTestSurvey({
        variables: {
          input: {
            language: selectedLanguage || '',
            topicId: testTopicId,
            topicGroupId,
          },
        },
        context: getTGAccessHeaderContext(search),
      });
      if (data) {
        window.open(data?.createTestSurvey, '_blank')?.focus();
      }
    } catch (error) {
      addToast(t('topicEditor:createTestSurveyFailed'), undefined, undefined, undefined, true);
      throw error;
    }
  };

  const copyUrlToClipboard = () => {
    ee.emitShareLinksModal(visibleShareLinks);
  };

  const edit = async () => {
    if (isEditable) {
      topicEditor.toFunc(topicGroupId);
    } else {
      const clonedTopic = await cloneTopicWithTopicGroup({
        variables: {
          id: topicId,
        },
      });
      topicEditor.toFunc(clonedTopic.data?.cloneTopicWithTopicGroup?.topicGroup.id!);
    }
  };

  const isNameInvalid = topicGroupTranslation !== undefined && topicGroupTranslation.length < 3;

  return (
    <>
      <StyledControlPanel>
        <div className="d-flex align-items-center">
          <EditTopicGroupInfo
            name={topicGroupTranslation}
            description={topicGroupDescription}
            setName={setTopicGroupName}
            setDescription={setTopicGroupDescription}
            preview={preview}
            isNameInvalid={isNameInvalid}
            isModalOpen={isDescriptionModalOpen}
            setModalOpen={setIsDescriptionModalOpen}
            allowedLanguages={languages}
          />
          <LanguageSelector
            lang={selectedLanguage}
            onChange={handleLanguageChange}
            onDelete={handleLanguageDelete}
            allowedLanguages={languages}
            showAddLanguageButton
            isAlignedToLeft
            displayAlways
            propsOverrideSelectedLanguage
            showDeleteOnLanguages={languages.filter((l) => l !== defaultTopicLanguageCode)}
            margin={`0 0 0 ${theme.spacing.d}`}
          />
          {hasAiTranslation && worstStatus && totalIssues > 0 && (
            <>
              <Tooltip
                title={t('topicEditor:translationIssuesBadge', {
                  missing: translationIssues.missing,
                  sameAsSource: translationIssues.sameAsSource,
                })}
              >
                <TranslationBadge worstStatus={worstStatus}>{totalIssues}</TranslationBadge>
              </Tooltip>
              {onTranslateAll && (
                <TranslateAllButton
                  disabled={isTranslatingAll}
                  onClick={async () => {
                    setIsTranslatingAll(true);
                    try {
                      await onTranslateAll();
                    } finally {
                      setIsTranslatingAll(false);
                    }
                  }}
                >
                  {isTranslatingAll ? '...' : t('topicEditor:translationTranslateAll')}
                </TranslateAllButton>
              )}
            </>
          )}
        </div>
        <div className="d-flex align-items-center">
          <StyledLinkButton
            disabled={loadingTestSurvey}
            onClick={createSurvey}
            data-icom="btn-test-survey-ariel"
            data-cy="topic-editor-test-survey"
          >
            <ChatIcon />
            <BodyMedium color={theme.colors.iconAction.primary} margin={`0 0 0 ${theme.spacing.d}`}>
              {t('topicEditor:tryTopic')}
            </BodyMedium>
          </StyledLinkButton>
          {((!preview && !limitedMode) || limitedMode === 'WRITE') && (
            <StyledLinkButton
              onClick={() => onSave(false)}
              data-cy="topic-editor-save"
              data-icom="btn-save-topic-ariel"
              disabled={isNameInvalid}
            >
              <DeprecatedSaveIcon />
              <BodyMedium color={theme.colors.iconAction.primary} margin={`0 0 0 ${theme.spacing.d}`}>
                {t('save')}
              </BodyMedium>
            </StyledLinkButton>
          )}
          {preview && isEditable && (
            <StyledLinkButton onClick={edit} data-icom="btn-edit-ariel" data-cy="btn-edit-ariel">
              <DeprecatedEditIcon />
              <BodyMedium color={theme.colors.iconAction.primary} margin={`0 0 0 ${theme.spacing.d}`}>
                {t('topicEditor:edit')}
              </BodyMedium>
            </StyledLinkButton>
          )}
          {!limitedMode && visibleShareLinks.length > 0 && (
            <StyledLinkButton onClick={copyUrlToClipboard} data-cy="topic-editor-share" data-icom="btn-share-ariel">
              <DeprecatedShareIcon />
              <BodyMedium color={theme.colors.iconAction.primary} margin={`0 0 0 ${theme.spacing.d}`}>
                {t('share')}
              </BodyMedium>
            </StyledLinkButton>
          )}
          {!preview && (
            <ActionMenuWrap>
              <ActionMenu
                dataCy="topic-editor-menu"
                dataIcom="btn-other-options-ariel"
                menuDataIcom="menu-other-options-ariel"
                toggleIcon={<DotMenuHorizontalIcon />}
                align="right"
                items={
                  [
                    !preview && {
                      label: t('topicEditor:clearEditor'),
                      action: () => setClearModalShow(true),
                    },
                    {
                      label: t('topicEditor:rearrange'),
                      action: onRearrange,
                    },
                    !preview && {
                      label: t('topicEditor:regenerateReportOrder'),
                      action: onRegenerateReportOrder,
                    },
                    !preview && {
                      label: t('topicEditor:editDescription'),
                      action: () => setIsDescriptionModalOpen(true),
                    },
                    !preview && {
                      label: t('topicEditor:downloadTranslations'),
                      action: onDownloadTranslations,
                    },
                    !preview && {
                      label: t('topicEditor:uploadTranslations'),
                      action: onUploadTranslations,
                    },
                  ].filter((item) => item) as ActionMenuItem[]
                }
              />
            </ActionMenuWrap>
          )}
          <ConditionalWrapper
            condition={!isValid || freeOrgWithCustomTopic}
            wrapper={(children) => (
              <Tooltip
                title={t(
                  !isValid ? 'topicCannotBeScheduled' : 'surveysOverview:freeOrganizationsCannotPlanCustomTopics',
                )}
              >
                {children}
              </Tooltip>
            )}
          >
            <ActionButton
              variant="primary"
              margin={`0 0 0 ${theme.spacing.f}`}
              onClick={() => history.push(`/createSurvey/TOPIC_GROUP/${topicGroupId}/${topicGroupTranslation}`)}
              data-icom={'btn-preview-plan-survey'}
              disabled={!isValid || freeOrgWithCustomTopic}
            >
              {t('topicEditor:plan')}
            </ActionButton>
          </ConditionalWrapper>
          {!limitedMode && (
            <StyledLinkButton onClick={onClose} data-icom="btn-close-ariel" data-cy="btn-close-ariel">
              <DeprecatedCloseIcon />
              <span className="visually-hidden">{t('close')}</span>
            </StyledLinkButton>
          )}
        </div>
      </StyledControlPanel>
      <Modal
        show={clearModalShow}
        onHide={() => setClearModalShow(false)}
        onSubmit={onClear}
        title={t('topicEditor:clearEditor')}
        text={t('topicEditor:clearEditorModal')}
        buttons={{
          submit: { title: t('topicEditor:clear') },
          cancel: { title: t('topicEditor:cancel') },
        }}
      />
    </>
  );
};
